import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import type { SquarePaymentsSnapshot } from "@shared/square-sales";

export type SquareSales = {
  source: "Square";
  status: "available" | "pending" | "incomplete" | "failed" | "unavailable";
  accountId: string;
  accountName: string;
  range: { since: string; until: string };
  currency: string | null;
  revenue: number | null;
  reportedRevenue?: number | null;
  payments?: SquarePaymentsSnapshot | null;
  locations: Array<{ locationId: string; locationName: string; timezone: string; currency: string }>;
  accountingBasis: string;
  updatedAt: string | null;
  limitation: string | null;
  missingDays: string[];
  comparison: {
    priorRevenue: number;
    change: number;
    changePercent: number | null;
    range: { since: string; until: string };
  } | null;
};

const statusCopy: Record<SquareSales["status"], string> = {
  available: "Excludes tips, includes tax.",
  pending: "Square sales are still being collected. Check back after the next refresh.",
  incomplete: "Some payment days could not be fully retrieved. The displayed amount is partial.",
  failed: "Square could not provide sales data for this report. Retry the report or contact an administrator.",
  unavailable: "Square sales are unavailable for this account or reporting window.",
};

function money(value: number | null, currency: string | null) {
  if (value == null) return "Unavailable";
  if (!currency) return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 2 }).format(value);
  } catch {
    return `${currency} ${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)}`;
  }
}

export function SquareSalesSection({ sales, isRefreshing = false, lineItemsRefreshing = false, children }: {
  sales: SquareSales;
  isRefreshing?: boolean;
  lineItemsRefreshing?: boolean;
  children?: ReactNode;
}) {
  const hasReportedAmount = sales.status === "available"
    && sales.revenue != null;
  const hasPartialAmount = sales.status === "incomplete" && sales.payments != null;
  const payments = sales.status === "incomplete" || sales.status === "available" ? sales.payments : null;
  return (
    <Card data-testid="square-sales-section" className="rounded-xl border-slate-200 shadow-sm">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 pb-2">
        <div className="min-w-0">
          <CardTitle className="text-base">Square sales</CardTitle>
        </div>
        <div className="flex min-h-6 min-w-[100px] items-center justify-end">
          {isRefreshing || lineItemsRefreshing
          ? <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" role="status" aria-live="polite">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              {sales.status === "pending" ? "Loading…" : "Refreshing…"}
            </span>
          : sales.status !== "available" && <Badge variant="outline" className="capitalize">
              {sales.status === "incomplete" ? "Partial" : sales.status}
            </Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div data-testid="square-collected-sales-tile" className="w-full max-w-[280px] rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2.5">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Collected sales</p>
            <p className="mt-1 truncate text-xl font-semibold tracking-tight text-foreground" data-testid="square-sales-revenue">
            {payments ? money(payments.collected, sales.currency) : hasReportedAmount ? money(sales.revenue, sales.currency) : "Unavailable"}
            </p>
            <p className="mt-1 text-xs leading-snug text-muted-foreground">{payments ? "Excludes tips, includes tax." : statusCopy[sales.status]}</p>
          </div>
          {payments && <p className="mt-1.5 text-xs text-muted-foreground">{payments.paymentCount} completed payments</p>}
          {hasPartialAmount && <p className="mt-1.5 text-xs leading-snug text-muted-foreground">{statusCopy.incomplete}</p>}
        </div>
        {sales.limitation && (
          <p className="text-xs leading-relaxed text-muted-foreground">{sales.limitation}</p>
        )}
        {sales.status === "incomplete" && sales.missingDays.length > 0 && (
          <p className="text-xs text-muted-foreground">{sales.missingDays.length} day{sales.missingDays.length === 1 ? "" : "s"} with incomplete coverage</p>
        )}
        {children}
      </CardContent>
    </Card>
  );
}
